import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpClient, HttpStatusCode } from '@angular/common/http';

import { DashboardService } from '../../../../../service/events/dashboard.service';
import { FincasApiService } from '../../../../../service/api/fincas-api.service';
import { ClientesApiService } from '../../../../../service/api/clientes-api.service';
import { ProductosApiService } from '../../../../../service/api/productos-api';

import { BaseForm } from '../../../../base-form';
import { StorageManager } from '../../../../../common/storage-manager.class';
import { environment } from '../../../../../../environments/environment';
import { build, buildParams, BuildTask, pool } from '../../../../../common/classes/request-builder';

import { FincasEnvironmentDefinition } from '../environment-variables';
import { FincasFormDefinition } from './form-definition';
import { ReactiveFormBuilderComponent } from '../../../../../common/components/reactive-form-builder/form-builder.component';

import { FincasModel } from '../../../../../models/form-common/fincas.module';
import { ClienteModel } from '../../../../../models/cliente.model';
import { BaseResponseModel } from 'src/app/models/base-response.model';
import { ReaModalModel } from 'src/app/models/fincas/rea.model';

const FINCA_NAME = environment.features.fincaName;


/** Minimal typing for server responses */
interface ReaImportData {
	parcelasNoInsertadas?: number;
	parcelasInsertadas?: number;
	parcelasNoActualizadas?: number;
	parcelasActualizadas?: number;
	errores?: Array<{ descripcion: string }>;
}

@Component({
	selector: 'app-finca-form',
	standalone: false,
	templateUrl: './finca-form.component.html',
	styleUrls: ['./finca-form.component.scss'],
})
export class FincaFormComponent extends BaseForm<FincasModel> implements OnInit {
	/***************************
	 * Form Builder Variables **
	 ***************************/
	public form!: FincasFormDefinition;

	public readonly formRequest!: ReactiveFormBuilderComponent<FincasModel>;
	/***************************************************************************** */


	/******************
	 * REA Variables **
	 ******************/
	public showReaImportDialog = false;

	public historicoREA = null;

	public readonly HTTP = HttpStatusCode;
	/***************************************************************************** */

	public readonly env: FincasEnvironmentDefinition = new FincasEnvironmentDefinition();
	
	private readonly formType = this.getType();


	/**
	 * Initializes the finca form container component, configures the inherited
	 * CRUD form workflow, and creates the reactive form request engine used by
	 * the builder layer to execute phase-based form actions.
	 *
	 * The injected services are intentionally split by responsibility:
	 * - routing services resolve the current navigation context and allow redirection,
	 * - dashboard services integrate the component into the parent form lifecycle,
	 * - API services provide entity persistence and dropdown data sources,
	 * - the HTTP client is passed to the reactive form builder request engine.
	 *
	 * @param router Router instance used to navigate after successful form actions
	 * and to transition between create, edit, and duplication flows.
	 * @param route Activated route that provides route parameters and contextual data
	 * required to resolve the current form request type and target entity identifier.
	 * @param dashboard Dashboard service consumed by the base form component to
	 * integrate this form into the shared page and navigation workflow.
	 * @param fincasApi API service that exposes finca CRUD endpoints and is used as
	 * the main persistence gateway for the current entity.
	 * @param clientesApi API service used to resolve and populate client-related
	 * sources required by the finca form configuration.
	 * @param productosApi API service used to resolve and populate product-related
	 * sources required by dependent form controls and auxiliary data flows.
	 * @param http Low-level HTTP client injected exclusively to instantiate the
	 * reactive form builder engine, which internally executes request phases.
	 */
	constructor(
		public override router: Router,
		public override route: ActivatedRoute,
		public override dashboard: DashboardService,
		public fincasApi: FincasApiService,
		public clientesApi: ClientesApiService,
		public productosApi: ProductosApiService,
		private readonly http: HttpClient,
	) {
		super(
			route,
			router,
			dashboard,
			fincasApi.fincasPUT,
			fincasApi.fincasPOST,
			'finca',
			`Editar ${FINCA_NAME}`,
			`Crear ${FINCA_NAME}`,
			`Duplicar ${FINCA_NAME}`,
		);
		this.formRequest = new ReactiveFormBuilderComponent(this.http);
	} // ();

	/**
	 * Initializes the finca form component by creating the form definition,
	 * loading dropdown data sources, and applying default client-related values
	 * based on the current environment configuration and form mode.
	 *
	 * The initialization flow performs the following steps:
	 * 1. Creates the reactive form definition and wires the REA import callback.
	 * 2. Starts asynchronous dropdown population required by the form controls.
	 * 3. Auto-fills client identification data when NIF autocomplete is enabled
	 *    and the form is opened in creation mode.
	 * 4. Assigns the current user's client identifier when REA integration is disabled.
	 *
	 * @returns void
	 */
	public ngOnInit(): void {
		this.form = new FincasFormDefinition(
			this.formType, 
			null, 
			{
				openReaImport: (): void => { this.showReaImportDialog = true; },
			}
		);

		void this.initializeDropdownData();

		if (!this.env.activateREA) {
			const loggedInClient = StorageManager.getUser();

			this.form.model.id_cliente = +loggedInClient.id;
		}// if();
	}// ();

	/**
	 * Updates the internal form state with the latest model snapshot emitted by the
	 * reactive form builder and stores the current form tag associated with that change.
	 *
	 * This method is typically invoked from the builder change event and keeps the
	 * local form definition synchronized with the current UI state.
	 *
	 * @param formModel Latest form model emitted by the reactive form builder.
	 * @param tag Identifier associated with the emitted form state or current form context.
	 * @returns void
	 */
	public formChanges(formModel: object, tag: string): void {
		this.form.model = formModel as FincasModel;
		this.form.tag = tag;
	}// ();

	/**
	 * Builds and initializes the asynchronous dropdown data sources required by the finca form.
	 *
	 * This method collects the list-building tasks enabled by the current environment,
	 * executes them as a pooled async batch, and notifies the reactive form builder once
	 * all dropdown sources are ready. When no tasks are required, it resolves immediately
	 * and still emits the completion notification to keep the builder lifecycle consistent.
	 *
	 * The completion notification is deferred to a microtask so the dropdown-finalization
	 * signal matches the timing semantics used after asynchronous pool completion.
	 *
	 * @returns A promise that resolves to `true` once all configured dropdown build tasks
	 * have completed and the finalization trigger has been scheduled.
	 */
	private initializeDropdownData(): Promise<boolean> {
		const builds: BuildTask[] = [];

		if (this.env.hasClients) {
			builds.push(
				build(
					this.clientesApi.clientesGET,
					this.form.lists.clientes,
					(it: ClienteModel) => `${it.nombre} ${it.apellidos || ''} ${it.apellido_1 || ''} ${it.apellido_2 || ''}`,
					false,
				),
			);
		}// if();

		if (this.env.showFincasProductos) {
			builds.push(
				buildParams(
					this.productosApi.productos.GET,
					{ tipo: 'fertilizante' },
					this.form.lists.productos,
					'nombre',
					false,
					true,
				),
			);
		}// ();

		const notify = (): void => {
			queueMicrotask(() => {
				this.dropdownsFinalized = true;
				this.dropdownLoadTrigger.next();
			});
		};

		// No tasks -> fire trigger efficiently and resolve
		if (builds.length === 0) {
			notify();
			return Promise.resolve(true);
		}// ();

		// Tasks exist -> fire trigger after pool resolves
		return pool(builds, notify);
	}
	/***************************************************************************** */

	/******************
	 * REA Functions **
	 *****************/
	/**
	 * Handles the result returned by the REA import workflow.
	 *
	 * When the import completes successfully with HTTP status `202`, this method
	 * triggers a finca data refresh so the UI can reflect the imported state.
	 *
	 * @param result Response wrapper returned by the REA import operation.
	 * @returns void
	 */
	public onReaImported(result: BaseResponseModel<ReaImportData>): void {
		if (result.statusCode === 202) {
			this.fincasApi.fincasGET.safePerform();
		}// if();
	}// ();

	/**
	 * Builds the input model required by the REA import modal from the current
	 * finca form state.
	 *
	 * This method extracts the REA code, holder tax identifier, finca name, and
	 * client identifier from the current form model, normalizing optional values
	 * to `null` when they are not available.
	 *
	 * @returns REA modal payload generated from the current finca form model.
	 */
	public buildReaModalModel(): ReaModalModel {
		return {
			codigo_REA: this.form.model.codigo_REA ?? null,
			titular_nif: this.form.model.nif ?? this.form.model.titular_nif ?? null,
			nombre_finca: this.form.model.nombre ?? null,
			id_cliente: +((this.form.model.id_cliente as unknown as ClienteModel).id)
		};
	}// ();
	/***************************************************************************** */
}// class;
